/**
 * BonusBadges — the shared pill language for event bonuses.
 *
 * One renderer for every surface that shows a bonus (Calendar cards, the
 * Profile catch/raid radar rows, the Bonuses page, the floating overlay), so a
 * "2× Catch Candy" pill looks identical everywhere. Follows the EventTypePill
 * recipe (colour + color-mix chip) in the `--bonus` pink, matching the
 * floating Bonus bubble: a bonus is a bonus wherever it appears, and pink
 * means bonus nowhere else in the app.
 */
import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Candy,
  Clover,
  Egg,
  Flame,
  Gift,
  MapPin,
  PawPrint,
  Sparkles,
  Swords,
  Zap,
} from "lucide-react";
import type { BonusKind, ParsedBonus } from "@/lib/eventBonuses";

export const BONUS_KIND_ICONS: Record<BonusKind, LucideIcon> = {
  xp: Zap,
  stardust: Sparkles,
  candy: Candy,
  hatch: Egg,
  lucky: Clover,
  raid: Swords,
  trade: ArrowLeftRight,
  incense: Flame,
  lure: MapPin,
  encounter: PawPrint,
  other: Gift,
};

export function BonusPill({
  bonus,
  testId,
}: {
  bonus: ParsedBonus;
  testId?: string;
}) {
  const Icon = BONUS_KIND_ICONS[bonus.kind];
  // The text WRAPS instead of truncating (2026-08-28 ask): a bonus line like
  // "The maximum number of sets you can play per day will increase from five
  // to ten" is the whole point of the pill, so it must be readable in full.
  // items-start + mt keeps the icon on the first line when it does wrap.
  return (
    <span
      data-testid={testId}
      className="inline-flex items-start gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-semibold select-none max-w-full text-left"
      style={{
        color: "hsl(var(--bonus))",
        background: "hsl(var(--bonus) / 0.12)",
        border: "1px solid hsl(var(--bonus) / 0.34)",
      }}
      title={bonus.text}
    >
      <Icon className="w-3 h-3 shrink-0 mt-[1.5px]" />
      <span className="min-w-0 leading-snug break-words">{bonus.text}</span>
    </span>
  );
}

/**
 * Compact row of bonus pills for an event card. Caps at `max` pills with a
 * "+N more" chip that opens the rest in place (2026-09-18 owner ask: the
 * chip used to be text with a tooltip, which a phone cannot show); renders
 * nothing when the event has no bonuses, so it is always safe to drop into a
 * card unconditionally.
 */
export function BonusBadges({
  bonuses,
  max = 3,
  testId,
}: {
  bonuses: readonly ParsedBonus[] | undefined;
  max?: number;
  testId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!bonuses || bonuses.length === 0) return null;
  const shown = expanded ? bonuses : bonuses.slice(0, max);
  const overflow = bonuses.length - Math.min(bonuses.length, max);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 min-w-0" data-testid={testId}>
      {shown.map((b, i) => (
        <BonusPill key={`${b.text}-${i}`} bonus={b} />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={(e) => {
            // The card around this row is often a link; the chip is not.
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          className="text-[10px] font-semibold text-[var(--text-tertiary)] underline-offset-2 hover:underline"
          data-testid={testId ? `${testId}-more` : undefined}
        >
          {expanded ? "Show less" : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}
